import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, ChevronRight, Calendar, Users, MapPin } from 'lucide-react';
import { useApp } from '../context/AppContext';
import EventForm from '../components/EventForm';
import { format } from 'date-fns';

const EVENT_TYPE_LABELS = {
  conference: 'Conference',
  meetup: 'Meetup',
  dinner: 'Dinner',
  webinar: 'Webinar',
  networking: 'Networking',
  other: 'Other'
};

const EVENT_TYPE_COLORS = {
  conference: '#3b82f6',
  meetup: '#22c55e',
  dinner: '#f59e0b',
  webinar: '#8b5cf6',
  networking: '#1e3a5f',
  other: '#6b7280'
};

function Events() {
  const { loading, fetchEvents, createEvent } = useApp();

  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [timeframe, setTimeframe] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');

  const loadEvents = useCallback(async (filters = {}) => {
    const result = await fetchEvents(filters);
    setEvents(result);
  }, [fetchEvents]);

  useEffect(() => {
    loadEvents();
  }, []);

  const debounceRef = useRef(null);

  const handleSearch = useCallback((value) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadEvents({ search: value, timeframe, event_type: eventTypeFilter });
    }, 300);
  }, [timeframe, eventTypeFilter, loadEvents]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleTimeframeChange = (value) => {
    const newTimeframe = timeframe === value ? '' : value;
    setTimeframe(newTimeframe);
    loadEvents({ search, timeframe: newTimeframe, event_type: eventTypeFilter });
  };

  const handleTypeFilterChange = (value) => {
    setEventTypeFilter(value);
    loadEvents({ search, timeframe, event_type: value });
  };

  const handleSave = async (data) => {
    await createEvent(data);
    loadEvents({ search, timeframe, event_type: eventTypeFilter });
  };

  const formatEventDate = (dateStr) => {
    try {
      return format(new Date(dateStr + 'T00:00:00'), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const isUpcoming = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr >= today;
  };

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem' }}>
            Events
          </h1>
          <p className="text-sm text-muted">
            {events.length} event{events.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={18} />
          Create Event
        </button>
      </div>

      {/* Search and Filters */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="search-wrapper" style={{ flex: 1, minWidth: '200px' }}>
            <Search size={18} className="search-icon" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="form-input search-input"
              placeholder="Search events..."
            />
          </div>

          {/* Timeframe toggle chips */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className={`btn btn-sm ${timeframe === 'upcoming' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => handleTimeframeChange('upcoming')}
            >
              Upcoming
            </button>
            <button
              className={`btn btn-sm ${timeframe === 'past' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => handleTimeframeChange('past')}
            >
              Past
            </button>
          </div>

          {/* Event type filter */}
          <select
            value={eventTypeFilter}
            onChange={(e) => handleTypeFilterChange(e.target.value)}
            className="form-input form-select"
            style={{ width: 'auto', minWidth: '140px' }}
          >
            <option value="">All Types</option>
            {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Events List */}
      {loading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : events.length === 0 ? (
        <div className="card empty-state">
          <Calendar size={48} style={{ color: 'var(--gray-300)', marginBottom: '1rem' }} />
          <p>No events found</p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => setShowForm(true)}
          >
            Create Your First Event
          </button>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="events-table-view">
            <div className="card" style={{ padding: 0 }}>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Type</th>
                      <th className="mobile-hide">Date</th>
                      <th className="mobile-hide">Location</th>
                      <th className="mobile-hide">Attendees</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map(event => (
                      <tr key={event.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '0.5rem',
                              background: `${EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS.other}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Calendar size={20} style={{
                                color: EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS.other
                              }} />
                            </div>
                            <div>
                              <div className="font-medium">{event.title}</div>
                              {event.description && (
                                <div className="text-sm text-muted" style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '250px'
                                }}>
                                  {event.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              background: `${EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS.other}15`,
                              color: EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS.other
                            }}
                          >
                            {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                          </span>
                        </td>
                        <td className="mobile-hide">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <span className={isUpcoming(event.date) ? 'font-medium' : 'text-muted'}>
                              {formatEventDate(event.date)}
                            </span>
                            {isUpcoming(event.date) && (
                              <span className="badge" style={{
                                background: 'var(--success)15',
                                color: 'var(--success)',
                                fontSize: '0.625rem',
                                marginLeft: '0.25rem'
                              }}>
                                Upcoming
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="mobile-hide">
                          {event.location && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <MapPin size={14} className="text-muted" />
                              <span className="text-sm">{event.location}</span>
                            </div>
                          )}
                        </td>
                        <td className="mobile-hide">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <Users size={14} className="text-muted" />
                            <span className="text-sm">
                              {(event.attendee_ids || []).length}
                            </span>
                          </div>
                        </td>
                        <td>
                          <Link
                            to={`/events/${event.id}`}
                            className="btn btn-ghost btn-sm"
                          >
                            <ChevronRight size={18} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="events-card-view">
            {events.map(event => (
              <Link
                key={event.id}
                to={`/events/${event.id}`}
                className="event-card"
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '0.5rem',
                  background: `${EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS.other}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Calendar size={20} style={{
                    color: EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS.other
                  }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="font-medium">{event.title}</div>
                  <div className="text-sm text-muted">
                    {formatEventDate(event.date)}
                    {event.location && ` · ${event.location}`}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem', alignItems: 'center' }}>
                    <span
                      className="badge"
                      style={{
                        background: `${EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS.other}15`,
                        color: EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS.other,
                        fontSize: '0.6875rem'
                      }}
                    >
                      {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                    </span>
                    <span className="text-xs text-muted">
                      {(event.attendee_ids || []).length} attendee{(event.attendee_ids || []).length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <ChevronRight size={18} className="text-muted" />
              </Link>
            ))}
          </div>

          <style>{`
            .events-table-view {
              display: block;
            }
            .events-card-view {
              display: none;
            }
            @media (max-width: 768px) {
              .events-table-view {
                display: none;
              }
              .events-card-view {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
              }
              .event-card {
                display: flex;
                align-items: center;
                gap: 0.75rem;
                padding: 0.75rem;
                background: white;
                border-radius: 0.5rem;
                text-decoration: none;
                color: inherit;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
              }
              .event-card:hover {
                background: var(--gray-50);
              }
            }
          `}</style>
        </>
      )}

      {showForm && (
        <EventForm
          onClose={() => setShowForm(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

export default Events;
